import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  parseSocialLinks,
  usePracticeProfileQuery,
  useUpdatePracticeProfileMutation,
  type TenantSocialLink,
} from "../../apollo/practice";

/**
 * Social links settings -- the profiles shown on the public website (footer
 * social section). Lives under Practice; the chrome editor's footer social
 * accordion links here.
 */

// Common platforms offered as suggestions; the field stays free-text so any
// platform name works (the footer renders the name as the button label).
const PLATFORM_SUGGESTIONS = [
  "LinkedIn",
  "Facebook",
  "Instagram",
  "X",
  "YouTube",
  "TikTok",
  "Yelp",
  "Google Business",
];

export function SocialLinksPage() {
  const { data, loading, error } = usePracticeProfileQuery();
  const [updateProfile, { loading: saving, error: saveError }] =
    useUpdatePracticeProfileMutation();

  const savedLinks = data?.currentTenant?.socialLinks ?? null;

  const [links, setLinks] = useState<TenantSocialLink[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLinks(parseSocialLinks(savedLinks));
  }, [savedLinks]);

  const dirty =
    JSON.stringify(links) !== JSON.stringify(parseSocialLinks(savedLinks));

  const updateLink = (index: number, patch: Partial<TenantSocialLink>) => {
    setLinks((prev) =>
      prev.map((l, i) => (i === index ? { ...l, ...patch } : l)),
    );
  };

  const addLink = () => {
    setLinks((prev) => [...prev, { platform: "", url: "" }]);
  };

  const removeLink = (index: number) => {
    setLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    // Drop rows without a URL rather than persisting blanks
    const cleaned = links
      .map((l) => ({ platform: l.platform.trim(), url: l.url.trim() }))
      .filter((l) => l.url);
    void updateProfile({
      variables: { input: { socialLinks: JSON.stringify(cleaned) } },
    }).then(({ data: result }) => {
      if (result?.updateTenantConfig) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    });
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-[13px] text-ink-5">Loading social links…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-rust-s/40 px-3 py-2 text-[12.5px] text-rust">
        Failed to load social links: {error.message}
      </div>
    );
  }

  const fieldClass =
    "hair w-full rounded-md border bg-paper px-3 py-2 text-[13px] text-ink placeholder:text-ink-5 focus:border-tide focus:outline-none";
  const labelClass =
    "mb-1.5 block text-[11.5px] font-medium uppercase tracking-wider text-ink-4";

  return (
    <div className="grid grid-cols-12 gap-3">
      <section className="col-span-12 lg:col-span-8">
        <div className="mb-8">
          <h1 className="font-display text-[40px] font-medium leading-[1.05] tracking-tight">
            Social links
          </h1>
          <p className="mt-2 max-w-xl text-[14.5px] leading-relaxed text-ink-3">
            Profiles linked from your public website — shown in the footer's
            social section.
          </p>
        </div>

        <div className="space-y-8">
          <section className="shadow-card rounded-lg bg-paper-hi">
            <div className="hair-b border-b px-7 py-4">
              <h2 className="text-[14px] font-semibold text-ink">
                Your profiles
              </h2>
            </div>
            <div className="space-y-4 p-7">
              {links.length === 0 && (
                <p className="text-[13px] text-ink-4">
                  No social links yet. Add one to show it on your website.
                </p>
              )}
              {links.map((link, i) => (
                <div
                  key={i}
                  className="hair relative rounded-md border bg-paper p-4"
                >
                  <button
                    type="button"
                    onClick={() => removeLink(i)}
                    aria-label="Remove social link"
                    className="absolute right-3 top-3 text-ink-5 transition-colors hover:text-rust"
                  >
                    <Trash2 size={14} strokeWidth={1.8} />
                  </button>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-[180px_1fr] sm:pr-8">
                    <div>
                      <label className={labelClass}>Platform</label>
                      <input
                        type="text"
                        value={link.platform}
                        onChange={(e) =>
                          updateLink(i, { platform: e.target.value })
                        }
                        placeholder="LinkedIn"
                        list="social-platform-suggestions"
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Profile URL</label>
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => updateLink(i, { url: e.target.value })}
                        placeholder="https://www.linkedin.com/company/yourpractice"
                        className={fieldClass}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <datalist id="social-platform-suggestions">
                {PLATFORM_SUGGESTIONS.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
              <button
                type="button"
                onClick={addLink}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-tide hover:underline"
              >
                <Plus size={13} strokeWidth={2.2} />
                Add link
              </button>
            </div>
          </section>

          {/* Save bar */}
          <div className="flex items-center justify-end gap-3">
            {saveError && (
              <span className="text-[11.5px] text-rust">{saveError.message}</span>
            )}
            {saved && <span className="text-[11.5px] text-sage">Saved</span>}
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saving}
              className={`rounded-md px-4 py-1.5 text-[12.5px] font-medium ${
                dirty && !saving
                  ? "bg-ink text-paper-hi hover:bg-ink-2"
                  : "hair border bg-paper text-ink-5"
              }`}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
